import { useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { MobileBottomNav } from '../Stations/MobileBottomNav';
import { DateRangePicker } from '../../../components/common/DateRangePicker/DateRangePicker';
import { PrivacyToggle } from '../../../components/common/PrivacyToggle/PrivacyToggle';
import { usePrivacy } from '../../../context/PrivacyContext';
import { StationMultiSelect } from './StationMultiSelect';
import { OrganizationMultiSelect } from './OrganizationMultiSelect';
import { StationMetricsTable } from './StationMetricsTable';
import { MobileStationMetrics } from './MobileStationMetrics';
import { MetricsContent } from './MetricsContent';
import { StationOption } from '../../../services/ocpp/types';
import { OrganizationOption } from '../../../types/organization';
import { useDashboardStatsQuery, dashboardQueryKeys } from '../../../hooks/useDashboardQueries';
import { usePermissions } from '../../../hooks/usePermissions';
import { ErrorMessage } from '../../../components/common/ErrorMessage/ErrorMessage';

export const MetricsV2 = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { has, organization } = usePermissions();
  const showOrgFilter = has('organizations.view') && !organization;
  const [dateRange, setDateRange] = useState('This Month');
  // One read for the page; every figure below is masked from this.
  const { hidden } = usePrivacy('metrics');
  const [selectedStations, setSelectedStations] = useState<StationOption[]>([]);
  const [selectedOrganizations, setSelectedOrganizations] = useState<OrganizationOption[]>([]);

  const stationIds = selectedStations.length > 0
    ? selectedStations.map((s) => s.id).join(',')
    : undefined;

  const organizationIds = selectedOrganizations.length > 0
    ? selectedOrganizations.map((o) => o.id).join(',')
    : undefined;

  const {
    data: stats,
    isLoading: loading,
    isFetching,
    error,
  } = useDashboardStatsQuery({
    dateRange,
    stationIds,
    organizationIds,
  });

  const handleRefresh = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: dashboardQueryKeys.dashboardStats({ dateRange, stationIds, organizationIds }),
    });
    queryClient.invalidateQueries({
      queryKey: dashboardQueryKeys.kwhPerDay({ dateRange, stationIds, organizationIds }),
    });
  }, [queryClient, dateRange, stationIds, organizationIds]);

  const handleDateRangeChange = useCallback((value: string) => {
    setDateRange(value);
  }, []);

  const handleStationsChange = useCallback((stations: StationOption[]) => {
    setSelectedStations(stations);
  }, []);

  const handleOrganizationsChange = useCallback((orgs: OrganizationOption[]) => {
    setSelectedOrganizations(orgs);
    setSelectedStations([]);
  }, []);

  const errorMessage = error instanceof Error ? error.message : t('dashboard.metrics.errorLoading');
  const showLoading = loading || isFetching;

  return (
    <>
      {/* Desktop Layout */}
      <div className="desktop-layout">
        <div className={showLoading ? 'loading-dim' : ''}>
          <div className="filter-bar">
            <div className="filter-bar-inner">
              <div className="filter-controls">
                <h2 className="metrics-page-title">{t('dashboard.metrics.overview')}</h2>
                {showOrgFilter && (
                  <OrganizationMultiSelect
                    selectedOrganizations={selectedOrganizations}
                    onChange={handleOrganizationsChange}
                  />
                )}
                <StationMultiSelect
                  selectedStations={selectedStations}
                  onChange={handleStationsChange}
                  organizationIds={organizationIds}
                />
                <DateRangePicker value={dateRange} onChange={handleDateRangeChange} />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <PrivacyToggle scope="metrics" />
                <button
                  type="button"
                  className="refresh-btn"
                  onClick={handleRefresh}
                  aria-label={t('dashboard.metrics.refreshAria')}
                >
                  <span className="material-symbols-outlined" aria-hidden="true">refresh</span>
                </button>
              </div>
            </div>
          </div>
        </div>
        <div className="content-inner" style={{ padding: '2.5rem' }}>
          {error ? (
            <ErrorMessage message={errorMessage} onRetry={handleRefresh} />
          ) : (
            <MetricsContent
              stats={stats}
              loading={loading}
              dateRange={dateRange}
              stationIds={stationIds}
              organizationIds={organizationIds}
              statsGridClassName="stats-grid"
              hidden={hidden}
              tableSlot={
                <StationMetricsTable
                  metrics={stats?.stationMetrics || []}
                  loading={loading}
                  hidden={hidden}
                />
              }
            />
          )}
        </div>
      </div>

      {/* Mobile Layout */}
      <div className="mobile-layout">
        <header className="mobile-header">
          <div className="mobile-header-top">
            <h1 className="mobile-header-title">{t('dashboard.sidebar.metrics')}</h1>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
              <PrivacyToggle scope="metrics" />
              <button
                type="button"
                className="mobile-refresh-btn"
                onClick={handleRefresh}
                aria-label={t('dashboard.metrics.refreshAria')}
              >
                <span className="material-symbols-outlined" aria-hidden="true">refresh</span>
              </button>
            </div>
          </div>
          <div className="mobile-filter-row" style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            {showOrgFilter && (
              <OrganizationMultiSelect
                selectedOrganizations={selectedOrganizations}
                onChange={handleOrganizationsChange}
              />
            )}
            <StationMultiSelect
              selectedStations={selectedStations}
              onChange={handleStationsChange}
              organizationIds={organizationIds}
            />
            <DateRangePicker value={dateRange} onChange={handleDateRangeChange} />
          </div>
        </header>

        <main className="mobile-content">
          {error ? (
            <ErrorMessage message={errorMessage} onRetry={handleRefresh} />
          ) : (
            <MetricsContent
              stats={stats}
              loading={loading}
              dateRange={dateRange}
              stationIds={stationIds}
              organizationIds={organizationIds}
              statsGridClassName="mobile-stats-grid"
              hidden={hidden}
              tableSlot={
                <MobileStationMetrics
                  metrics={stats?.stationMetrics || []}
                  loading={loading}
                  hidden={hidden}
                />
              }
            />
          )}
        </main>

        <MobileBottomNav />
      </div>
    </>
  );
};

export default MetricsV2;
